import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CalendarDays, GraduationCap, MapPin, Plus, Users } from 'lucide-react';
import {
  ContextHero,
  DataCard,
  DateBadge,
  EmptyStateModern,
  KpiCard,
  MoneyAmount,
  PageContainer,
  SectionTitle,
  SkeletonShimmer,
  StatusBadge,
  ActionButton,
  type UniversalStatus,
} from '@/components/design-system';
import { useFormationB2BKpis, useFormationB2BSessions } from '@/hooks/queries/useFormationB2B';

const STATUT_TONE: Record<string, UniversalStatus> = {
  planifiee: 'planifie',
  ouverte_inscriptions: 'en_cours',
  complete: 'fait',
  en_cours: 'en_cours',
  terminee: 'paye',
  annulee: 'annule',
  reportee: 'en_attente',
};

export function SessionsTab(): JSX.Element {
  const { data: k, isLoading: kpisLoading } = useFormationB2BKpis();
  const { data: sessions = [], isLoading } = useFormationB2BSessions();

  return (
    <PageContainer>
      <div className="space-y-6">
        <ContextHero
          variant="formation_b2b"
          icon={<GraduationCap className="h-5 w-5" />}
          title="Formation B2B — Formateurs & Sessions"
          subtitle="Pilotez vos formateurs, planifiez vos sessions et suivez le remplissage en temps réel."
          actions={
            <Link to="/formation/sessions">
              <ActionButton
                variant="gradient"
                module="formation_b2b"
                icon={<Plus className="h-4 w-4" />}
              >
                Nouvelle session
              </ActionButton>
            </Link>
          }
        />

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <KpiCard
            variant="formation_b2b"
            label="Sessions à venir"
            value={k?.sessions_a_venir ?? 0}
            icon={<CalendarDays className="h-4 w-4" />}
            loading={kpisLoading}
          />
          <KpiCard
            variant="formation_b2b"
            label="Sessions en cours"
            value={k?.sessions_en_cours ?? 0}
            icon={<CalendarDays className="h-4 w-4" />}
            loading={kpisLoading}
          />
          <KpiCard
            variant="formation_b2b"
            label="Formateurs actifs"
            value={k?.formateurs_actifs ?? 0}
            icon={<Users className="h-4 w-4" />}
            loading={kpisLoading}
          />
          <KpiCard
            variant="formation_b2b"
            label="Taux remplissage moyen"
            value={`${(k?.taux_remplissage_avg ?? 0).toFixed(0)}%`}
            icon={<Users className="h-4 w-4" />}
            loading={kpisLoading}
          />
        </div>

        <SectionTitle
          trailing={
            <div className="flex gap-2">
              <Link
                to="/formation/formateurs"
                className="inline-flex items-center gap-1 rounded-lg bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50"
              >
                Liste formateurs →
              </Link>
              <Link
                to="/formation/catalogue"
                className="inline-flex items-center gap-1 rounded-lg bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50"
              >
                Catalogue →
              </Link>
            </div>
          }
        >
          {sessions.length} session{sessions.length > 1 ? 's' : ''}
        </SectionTitle>

        {isLoading ? (
          <SkeletonShimmer variant="card" count={3} />
        ) : sessions.length === 0 ? (
          <EmptyStateModern
            variant="formation_b2b"
            icon={<CalendarDays className="h-7 w-7" />}
            title="Aucune session B2B"
            description="Créez votre première session : pré-remplit auto un dossier ROI lié pour suivre le bénéfice."
            ctaLabel="Nouvelle session"
            onCtaClick={() => (window.location.href = '/formation/sessions')}
          />
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {sessions.map((s, i) => (
              <motion.div
                key={s.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(i, 8) * 0.03, ease: [0.16, 1, 0.3, 1] }}
              >
                <Link to={`/formation/sessions/${s.id}`}>
                  <DataCard interactive>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                          {s.reference}
                        </div>
                        <h3 className="mt-0.5 line-clamp-2 text-base font-bold text-slate-900">
                          {s.formation_titre ?? '—'}
                        </h3>
                      </div>
                      <StatusBadge
                        status={STATUT_TONE[s.statut] ?? 'brouillon'}
                        label={s.statut.replace(/_/g, ' ')}
                        withDot
                      />
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                          Période
                        </div>
                        <div className="mt-0.5">
                          <DateBadge date={s.date_debut} /> → <DateBadge date={s.date_fin} />
                        </div>
                      </div>
                      <div>
                        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                          Inscrits
                        </div>
                        <div className="mt-0.5 font-bold text-slate-900">
                          {s.nb_inscrits} / {s.capacite_max}
                        </div>
                      </div>
                      {s.formateur_nom && (
                        <div className="col-span-2 flex items-center gap-1.5 text-slate-600">
                          <Users className="h-3 w-3" /> {s.formateur_prenom} {s.formateur_nom}
                        </div>
                      )}
                      {s.lieu_adresse && (
                        <div className="col-span-2 flex items-center gap-1.5 text-slate-600">
                          <MapPin className="h-3 w-3" />
                          <span className="line-clamp-1">{s.lieu_adresse}</span>
                        </div>
                      )}
                      {s.prix_session != null && (
                        <div className="col-span-2 mt-1">
                          <MoneyAmount amount={s.prix_session} variant="inline" />
                        </div>
                      )}
                    </div>
                  </DataCard>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </PageContainer>
  );
}
